import { Button, MultiSelectDropDown, RadioButton, TextInput } from '@wot/components';
import { ICategory, IHobby, IHobbyType, ILevels } from '@wot/typings';
import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import categoryApi, { CategoryReqBody } from '../../../api/categories';
import { CLOSE_ICON } from '../../../assets/icons';
import { MainBody } from '../../../components/MainBody';
import BreadCrumb from '../../../components/BreadCrumb';

const levelOptions: ILevels[] = ['beginner', 'intermediate', 'advance'];

export function AddHobby() {
  const [tempHobbies, setTempHobbies] = useState<IHobby[]>([]);
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [categories, setCategories] = useState<ICategory<IHobby>[]>([]);

  useEffect(() => {
    categoryApi
      .getCategories('hobby')
      .then((data) => {
        if (data) {
          setCategories(data);
        }
      })
      .catch((err) => err);
  }, []);


  const checkIfExists = (temp: IHobby[], categories: ICategory<IHobby>[], newValue: IHobby) => {
    let exists = temp.filter((s) => s.name == newValue.name).length > 0;
    if (exists) {
      toast.error(`${newValue.name} Hobby already added`)
      return true;
    }
    let existsInDB = categories.filter((s) => s.category.name == newValue.name).length > 0;
    if (existsInDB) {
      toast.error(`${newValue.name} Hobby already exists.`)
      return true;
    }
  }

  const handleFormChange = (newValues: IHobby, index?: number, remove?: number) => {
    let temp = [...tempHobbies];
    if (index) {
      if (remove) {
        temp = temp.filter((item, idx) => idx !== index - 1);
        setTempHobbies(temp);
        return;
      }
      let stemp = temp.filter((_, i) => i !== index - 1);
      const exists = checkIfExists(stemp, categories, newValues);
      if (exists) return;
      temp[index - 1] = newValues;
      setTempHobbies(temp);
    } else {
      const exists = checkIfExists(temp, categories, newValues);
      if (exists) return;
      temp.push(newValues);
      setTempHobbies(temp);
    }
  };

  useEffect(() => {
    let error = tempHobbies.some(
      (item) =>
        !item.name ||
        (!item.types?.length && item.levels?.length === 0) ||
        (item.types && item.types?.length > 0 && item.types.some((type) => !type.name || type.levels.length === 0)),
      []
    );
    if (error) {
      setError(error);
      return;
    } else {
      setError(false);
    }
  }, [tempHobbies]);
  const addHobbies = () => {
    let hobbies: CategoryReqBody[] = [];
    tempHobbies.forEach((x) => {
      hobbies.push({
        type: 'hobby',
        category: x
      });
    });

    categoryApi
      .addCategories(hobbies)
      .then((data) => {
        if (data) {
          navigate(-1);
          toast.success('Hobby added successfully!');
        } else {
          toast.error('Something went wrong');
        }
      })
      .catch(() => {
        toast.error('Something went wrong');
      });
  };

  return (
    <MainBody>
      <div className="relative w-full flex flex-col p-4 px-6 bg-white">
        <BreadCrumb />
        <CurrentHobbyForm handleFormChange={handleFormChange} />
        <div className="w-full h-[2px] bg-neutralGray1 my-10"></div>
        <div className="flex flex-col gap-5">
          {tempHobbies.map((hobby, index) => (
            <HobbyItem hobby={hobby} handleFormChange={handleFormChange} index={index + 1} key={index} />
          ))}
        </div>

        {tempHobbies.length > 0 && (
          <div className="w-full bg-white py-5">
            <div className="max-w-sm w-full mx-auto flex gap-4 items-center justify-center">
              <Button
                type="s-outlined"
                text={'Cancel'}
                onClick={() => navigate('/general-management/categories?type=hobbies')}
              />
              <Button disabled={error} text={'Save'} onClick={() => addHobbies()} />
            </div>
          </div>
        )}
      </div>
    </MainBody>
  );
}

export function CurrentHobbyForm({
  handleFormChange
}: {
  handleFormChange: (newValues: IHobby, index?: number) => void;
}) {
  const [currentHobby, setCurrentHobby] = useState<IHobby>({
    name: '',
    levels: [],
    types: []
  });
  const [errors, setErrors] = useState<HobbyFormError>();

  return (
    <div className="w-full flex flex-col my-5 gap-7">
      <div className="w-full flex items-center justify-between">
        <h1 className="text-xl font-semibold">Hobby</h1>
        <div className="max-w-xs">
          <Button
            text="+ Add New Hobby"
            onClick={() => {
              if (
                !currentHobby.name ||
                (currentHobby.types?.length === 0 && !currentHobby.levels?.length) ||
                (currentHobby.levels?.length === 0 && !currentHobby.types?.length)
              ) {
                setErrors({
                  name: !currentHobby.name,
                  levels: currentHobby.types?.length === 0 && !currentHobby.levels?.length,
                  types: currentHobby.levels?.length === 0 && !currentHobby.types?.length
                });
                return;
              }
              setErrors(undefined);
              handleFormChange(currentHobby);
              setCurrentHobby({
                name: '',
                levels: [],
                types: []
              });
            }}
          />
        </div>
      </div>
      <HobbyForm
        name={currentHobby.name}
        setName={(value) => setCurrentHobby({ ...currentHobby, name: value })}
        levelsList={currentHobby.levels || []}
        setLevelsList={(list) => setCurrentHobby({ ...currentHobby, levels: list })}
        types={currentHobby.types!}
        setTypes={(types) => setCurrentHobby({ ...currentHobby, types: types })}
        errors={errors}
      />
    </div>
  );
}
interface FormProps {
  hobby: IHobby;
  handleFormChange: (v: IHobby, index?: number, remove?: number) => void;
  index?: number;
}
export function HobbyItem({ hobby, handleFormChange, index }: FormProps) {
  const [errors, setErrors] = useState<HobbyFormError>();

  useEffect(() => {
    if (
      !hobby.name ||
      (hobby.types?.length === 0 && !hobby.levels?.length) ||
      (hobby.levels?.length === 0 && !hobby.types?.length)
    ) {
      setErrors({
        name: !hobby.name,
        levels: hobby.types?.length === 0 && !hobby.levels?.length,
        types: hobby.levels?.length === 0 && !hobby.types?.length
      });
      return;
    } else {
      setErrors(undefined);
    }
  }, [hobby]);

  return (
    <div className="w-full flex items-start gap-5 my-5">
      <HobbyForm
        name={hobby.name}
        setName={(value) => handleFormChange({ ...hobby, name: value }, index)}
        levelsList={hobby.levels || []}
        setLevelsList={(list) => handleFormChange({ ...hobby, levels: list }, index)}
        types={hobby.types!}
        setTypes={(types) => handleFormChange({ ...hobby, types: types }, index)}
        errors={errors}
      />
      <MdClose className="w-4 cursor-pointer" onClick={() => handleFormChange(hobby, index, 1)} />
    </div>
  );
}

export interface HobbyFormError {
  name?: boolean;
  levels?: boolean;
  types?: boolean;
  strengths?: boolean;
  experience?: boolean;
}

interface HobbyFormProps {
  name: string;
  setName: (value: string) => void;
  levelsList: ILevels[];
  setLevelsList: (levels: ILevels[]) => void;
  types: IHobbyType[];
  setTypes: (types: IHobbyType[]) => void;
  errors?: HobbyFormError;
}

export function HobbyForm({ name, setName, levelsList, setLevelsList, types, setTypes, errors }: HobbyFormProps) {
  const [isType, setIsType] = useState<'yes' | 'no'>(types && types.length > 0 ? 'yes' : 'no');

  const handleTypeChange = (newValues: IHobbyType, index?: number, remove?: number) => {
    let temp = [...types];
    if (index) {
      if (remove) {
        temp = temp.filter((item, idx) => idx !== index - 1);
        setTypes(temp);
        return;
      }
      temp[index - 1] = newValues;
      setTypes(temp);
    } else {
      temp.push(newValues);
      setTypes(temp);
    }
  };

  useEffect(() => {
    if (isType === 'yes') {
      setLevelsList([]);
    } else {
      setTypes([]);
    }
  }, [isType]);

  return (
    <div className="w-full max-w-lg flex flex-col my-5 gap-7">
      <div className="flex items-center justify-between">
        <h3 className="w-1/3 text-sm font-medium shrink-0">Hobby Name</h3>
        <div className="w-full max-w-md flex flex-col">
          <TextInput value={name} onChange={(e: any) => setName(e)} placeholder="Add Hobby" type="text" />
          {errors && errors?.name && <p className="text-xs font-medium text-red-400">Please Enter a Hobby Name</p>}
        </div>
      </div>

      {isType === 'no' && (
        <div className="flex items-center justify-between">
          <h3 className="w-1/3 text-sm font-medium shrink-0">Levels</h3>
          <div className="w-full h-full flex flex-col">
            <MultiSelectDropDown
              placeholder="Add Levels"
              onChange={(values: any) => setLevelsList(values)}
              values={levelsList}
              options={levelOptions}
              selectAll
            />
            {errors && errors?.levels && (
              <p className="text-xs font-medium text-red-400">Please Select one of the Levels</p>
            )}
          </div>
        </div>
      )}

      <div className="w-full flex flex-col">
        <div className="flex items-center justify-between">
          <h3 className="w-1/3 text-sm shrink-0 font-semibold">Add a Type?</h3>
          <div className="w-full h-full flex">
            <div className="flex items-center gap-2">
              <RadioButton
                checked={isType === 'yes'}
                onChange={(x) => x && setIsType('yes')}
                name="radio-buttons"
                label="Yes"
              />
              <RadioButton
                checked={isType === 'no'}
                onChange={(x) => x && setIsType('no')}
                name="radio-buttons"
                label="No"
              />
            </div>
          </div>
        </div>
        {isType === 'yes' && errors && errors?.types && (
          <p className="text-xs font-medium text-red-400">Please Add at least one Type</p>
        )}
      </div>

      {isType === 'yes' && (
        <>
          <CurrentTypeForm handleTypeChange={handleTypeChange} />
          {types.map((type, index) => (
            <div key={index} className="flex flex-col gap-2 border-t py-4">
              <HobbyTypeItem type={type} handleTypeChange={handleTypeChange} index={index + 1} key={type.name} />
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function CurrentTypeForm({ handleTypeChange }: { handleTypeChange: (newValues: IHobbyType, index?: number) => void }) {
  const [name, setName] = useState<string>('');
  const [levels, setLevelsList] = useState<ILevels[]>([]);

  return (
    <div className="w-full flex flex-col gap-4">
      <HobbyTypeForm
        name={name}
        setName={(value) => setName(value)}
        levels={levels}
        setLevelsList={(list) => setLevelsList(list)}
      />
      <div className="w-max">
        <Button
          text="+ Add Another Type"
          type="s-outlined"
          size="sm"
          onClick={() => {
            if (!name || !levels.length) {
              return;
            }
            handleTypeChange({ name, levels });
            setName('');
            setLevelsList([]);
          }}
        />
      </div>
    </div>
  );
}

function HobbyTypeItem({
  type,
  handleTypeChange,
  index
}: {
  type: IHobbyType;
  handleTypeChange: (newValues: IHobbyType, index?: number, remove?: number) => void;
  index: number;
}) {
  const [name, setName] = useState(type.name || '');
  const [levelsList, setLevelsList] = useState(type.levels || []);

  useEffect(() => {
    if (index) {
      handleTypeChange(
        {
          name,
          levels: levelsList
        },
        index
      );
    } else {
      handleTypeChange({
        name,
        levels: levelsList
      });
    }
  }, [name, levelsList]);

  return (
    <div className="flex items-start gap-2">
      <HobbyTypeForm
        name={name}
        setName={(value) => setName(value)}
        levels={levelsList}
        setLevelsList={(list) => setLevelsList(list)}
      />
      <img
        src={CLOSE_ICON}
        alt="delete type"
        className="w-4 cursor-pointer"
        onClick={() =>
          handleTypeChange(
            {
              name,
              levels: levelsList
            },
            index,
            1
          )
        }
      />
    </div>
  );
}
interface HobbyTypeFormProps {
  name: string;
  levels: ILevels[];
  setName: (value: string) => void;
  setLevelsList: (list: ILevels[]) => void;
}

function HobbyTypeForm({ name, levels, setName, setLevelsList }: HobbyTypeFormProps) {
  return (
    <div className="w-full flex flex-col gap-7">
      <div className="flex items-center justify-between">
        <h3 className="w-1/3 text-sm font-medium shrink-0">Type</h3>
        <div className="w-full max-w-md">
          <TextInput value={name} onChange={(e: any) => setName(e)} placeholder="Add Type" type="text" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <h3 className="w-1/3 text-sm font-medium shrink-0">Levels</h3>
        <div className="w-full h-full">
          <MultiSelectDropDown
            placeholder="Add Levels"
            onChange={(values: any) => setLevelsList(values)}
            values={levels}
            options={levelOptions}
            selectAll
          />
        </div>
      </div>
    </div>
  );
}
